import { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'
import { Button, Card, Stack } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useLocationHelpers from '@/hooks/useLocationHelpers'
import { OverlayState } from '@/hooks/useOverlay'
import {
  useCreateWorkflowStateWithDefaults,
  useUpdateWorkflowState,
} from '@/service-library/hooks/workflow-states'
import queryKeys from '@/service-library/query-keys'
import { themeColorOptions } from '@/theme/usePixydocsTheme'
import { WorkflowState, WorkflowStateType } from '@/types/workflows'
import generateUuid from '@/utils/uuid'
import { FormTextField, PixyDocsForm } from '@/components/forms'
import { Dialog, DialogContent, DialogFooter } from '../dialog'
import PixyColorPicker from '../PixyColorPicker'

type NewWorkflowStateDialogProps = {
  overlay: OverlayState
  workflowId: string
  workflowState?: WorkflowState
  detailQueryKey?: QueryKey
  listQueryKey?: QueryKey
  workflowStateType?: WorkflowStateType
  onCreateWorkflowState?: (state: WorkflowState) => void
  newWorkflowStateSortOrder?: number
}

type FormValues = {
  name: string
  color: string
}

export default function AddEditWorkflowStateDialog({
  overlay,
  workflowId,
  workflowState,
  detailQueryKey,
  listQueryKey,
  workflowStateType,
  onCreateWorkflowState,
  newWorkflowStateSortOrder,
}: NewWorkflowStateDialogProps) {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { appendCurrentParamsToUrl } = useLocationHelpers()

  const { createWorkflowState } = useCreateWorkflowStateWithDefaults({
    listQueryKey,
    sideEffectQueryKeys: [queryKeys.workflows.lists()],
    onSuccess: (state) => {
      onCreateWorkflowState?.(state)
      navigate(
        appendCurrentParamsToUrl(
          `/projects/${projectId}/settings/workflow-states/${state.id}`,
        ),
      )
    },
  })
  const { updateWorkflowState } = useUpdateWorkflowState({
    detailQueryKey,
    listQueryKey,
    sideEffectQueryKeys: [queryKeys.workflows.lists()],
  })

  const methods = useForm({
    defaultValues: {
      name: workflowState?.name || '',
      color: workflowState?.color || 'blue',
    },
  })

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    const id = generateUuid()
    if (workflowState) {
      updateWorkflowState({
        ...workflowState,
        name: values.name,
        color: values.color,
      })
    } else {
      createWorkflowState({
        id,
        code: id, // Defaulting code to UUID since they must be unique and we may not have a use for it later
        sort_order: newWorkflowStateSortOrder ?? 1000000,
        workflow_id: workflowId,
        name: values.name,
        color: values.color,
        default_document_view: 'validation',
        allowed_document_views: ['validation'],
        workflow_state_type_id: workflowStateType?.id,
        workflow_state_type: workflowStateType,
      })
    }

    overlay.close()
  }

  const { isSubmitSuccessful, isDirty, isValid } = methods.formState
  useEffect(() => {
    methods.reset({
      name: workflowState?.name || '',
      color: workflowState?.color || 'blue',
    })
  }, [
    overlay.isOpen,
    isSubmitSuccessful,
    methods,
    workflowState?.color,
    workflowState?.name,
  ])

  return (
    <Dialog
      title={workflowState ? 'Edit Workflow State' : 'New Workflow State'}
      {...overlay}
      maxWidth="xs"
    >
      <PixyDocsForm methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <FormTextField
              name="name"
              autoFocus
              label="Name"
              fullWidth
              required
              useControllerProps={{
                rules: {
                  validate: (value) => value.trim().length > 0,
                },
              }}
            />

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              <Card
                elevation={0}
                variant="outlined"
                sx={{ p: 2, alignSelf: 'center' }}
              >
                <PixyColorPicker
                  colors={themeColorOptions}
                  selectedColor={methods.watch('color')}
                  setSelectedColor={(color) => {
                    methods.setValue('color', color, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }}
                />
              </Card>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <Button type="submit" disabled={!isValid || !isDirty}>
            Submit
          </Button>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
